import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RefreshCw } from 'lucide-react';
import { ImageUpload } from './ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_SCHEMAS = {
  home: {
    label: 'Home Page',
    sections: [
      {
        title: 'Hero Section',
        fields: [
          { key: 'hero_overline', label: 'Hero Overline', type: 'text', placeholder: 'Eternal Wisdom for Modern Times' },
          { key: 'hero_title', label: 'Hero Title (Line 1)', type: 'text', placeholder: 'Discover the Timeless Path of' },
          { key: 'hero_highlight', label: 'Hero Highlight (Gold)', type: 'text', placeholder: 'Sanatana Dharma' },
          { key: 'hero_description', label: 'Hero Description', type: 'textarea' },
          { key: 'hero_image', label: 'Hero Background Image', type: 'image', recommended: '1920×1080 (16:9), high-quality landscape' },
        ],
      },
      {
        title: 'Introduction Section',
        fields: [
          { key: 'intro_overline', label: 'Intro Overline', type: 'text', placeholder: 'Welcome to Our Foundation' },
          { key: 'intro_heading', label: 'Intro Heading', type: 'text' },
          { key: 'intro_text_1', label: 'Intro Paragraph 1', type: 'textarea' },
          { key: 'intro_text_2', label: 'Intro Paragraph 2', type: 'textarea' },
          { key: 'intro_image', label: 'Intro Section Image', type: 'image', recommended: '800×600 (4:3), square-friendly' },
        ],
      },
      {
        title: 'Call to Action',
        fields: [
          { key: 'cta_heading', label: 'CTA Heading', type: 'text' },
          { key: 'cta_description', label: 'CTA Description', type: 'textarea' },
          { key: 'cta_image', label: 'CTA Background Image', type: 'image', recommended: '1920×800 (24:10), landscape' },
        ],
      },
    ],
  },
  about: {
    label: 'About Us Page',
    sections: [
      {
        title: 'Hero Section',
        fields: [
          { key: 'hero_overline', label: 'Hero Overline', type: 'text', placeholder: 'Our Story' },
          { key: 'hero_title', label: 'Hero Title (Line 1)', type: 'text' },
          { key: 'hero_highlight', label: 'Hero Highlight (Gold)', type: 'text' },
          { key: 'hero_description', label: 'Hero Description', type: 'textarea' },
          { key: 'hero_image', label: 'Hero Background Image', type: 'image', recommended: '1920×1080 (16:9)' },
        ],
      },
      {
        title: 'Mission Section',
        fields: [
          { key: 'mission_overline', label: 'Mission Overline', type: 'text' },
          { key: 'mission_heading', label: 'Mission Heading', type: 'text' },
          { key: 'mission_text_1', label: 'Mission Paragraph 1', type: 'textarea' },
          { key: 'mission_text_2', label: 'Mission Paragraph 2', type: 'textarea' },
          { key: 'mission_text_3', label: 'Mission Paragraph 3', type: 'textarea' },
          { key: 'mission_image', label: 'Mission Section Image', type: 'image', recommended: '800×600 (4:3)' },
        ],
      },
      {
        title: 'Vision Section',
        fields: [
          { key: 'vision_overline', label: 'Vision Overline', type: 'text', placeholder: 'Our Vision' },
          { key: 'vision_sanskrit', label: 'Sanskrit Mantra (optional)', type: 'text', placeholder: 'लोकाः समस्ताः सुखिनो भवन्तु' },
          { key: 'vision_statement_part_1', label: 'Vision Statement — Part 1', type: 'text', placeholder: 'Global Transformation' },
          { key: 'vision_connector', label: 'Connector Word', type: 'text', placeholder: 'Through' },
          { key: 'vision_statement_part_2', label: 'Vision Statement — Part 2', type: 'text', placeholder: 'Individual Reformation' },
          { key: 'vision_description', label: 'Vision Description', type: 'textarea' },
        ],
      },
      {
        title: 'The Five Pillars of Sanatana Dharma',
        fields: [
          { key: 'pillars_overline', label: 'Section Overline', type: 'text', placeholder: 'Foundation of Our Teachings' },
          { key: 'pillars_heading', label: 'Section Heading', type: 'text', placeholder: 'The Five Pillars of Sanatana Dharma' },
          { key: 'pillars_description', label: 'Section Description', type: 'textarea' },
          { key: 'pillar_1_title', label: 'Pillar 1 — Title', type: 'text', placeholder: 'Health' },
          { key: 'pillar_1_description', label: 'Pillar 1 — Description', type: 'textarea' },
          { key: 'pillar_2_title', label: 'Pillar 2 — Title', type: 'text', placeholder: 'Wealth' },
          { key: 'pillar_2_description', label: 'Pillar 2 — Description', type: 'textarea' },
          { key: 'pillar_3_title', label: 'Pillar 3 — Title', type: 'text', placeholder: 'Harmony' },
          { key: 'pillar_3_description', label: 'Pillar 3 — Description', type: 'textarea' },
          { key: 'pillar_4_title', label: 'Pillar 4 — Title', type: 'text', placeholder: 'Knowledge' },
          { key: 'pillar_4_description', label: 'Pillar 4 — Description', type: 'textarea' },
          { key: 'pillar_5_title', label: 'Pillar 5 — Title', type: 'text', placeholder: 'Self-realization' },
          { key: 'pillar_5_description', label: 'Pillar 5 — Description', type: 'textarea' },
        ],
      },
      {
        title: 'Closing Quote',
        fields: [
          { key: 'quote_text', label: 'Quote Text', type: 'text' },
          { key: 'quote_source', label: 'Quote Source', type: 'text' },
        ],
      },
    ],
  },
  mentor: {
    label: 'Our Mentor Page',
    sections: [
      {
        title: 'Section 1 — Hero Banner',
        fields: [
          { key: 'page_overline', label: 'Overline (small caps)', type: 'text', placeholder: 'GUIDING LIGHT OF RISHIDEVISM' },
          { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'Risheeshwar Narendranji' },
          { key: 'page_subtitle', label: 'Page Subtitle / Tagline', type: 'textarea', placeholder: 'Bridging Ancient Vedic Science with Modern Consciousness…' },
        ],
      },
      {
        title: 'Section 2 — Mentor Profile',
        fields: [
          { key: 'profile_overline', label: 'Profile Overline', type: 'text', placeholder: 'About Guruji' },
          { key: 'mentor_name', label: 'Mentor Name', type: 'text', placeholder: 'Risheeshwar Narendranji' },
          { key: 'profile_headline', label: 'Profile Headline / Designation', type: 'text', placeholder: 'Distinguished Spiritual Reformer & Saint' },
          { key: 'mentor_image', label: 'Mentor Portrait Photo', type: 'image', recommended: '800×1000 (portrait 4:5), high quality' },
          { key: 'mentor_bio_1', label: 'Bio Paragraph 1', type: 'textarea' },
          { key: 'mentor_bio_2', label: 'Bio Paragraph 2', type: 'textarea' },
          { key: 'mentor_bio_3', label: 'Bio Paragraph 3 (optional)', type: 'textarea' },
        ],
      },
      {
        title: 'Section 3 — Featured Quote',
        fields: [
          { key: 'quote_text', label: 'Quote Text', type: 'textarea', placeholder: 'Consciousness governs energy, and energy rules the world.' },
          { key: 'quote_source', label: 'Attribution', type: 'text', placeholder: 'Risheeshwar Narendranji' },
        ],
      },
      {
        title: 'Section 3b — Tools We Teach the Seekers',
        fields: [
          { key: 'tools_overline', label: 'Overline', type: 'text', placeholder: 'Sacred Sadhana' },
          { key: 'tools_heading', label: 'Section Heading', type: 'text', placeholder: 'Tools We Teach the Seekers' },
          { key: 'tools_description', label: 'Section Description', type: 'textarea' },
          { key: 'tool_1_title', label: 'Tool 1 — Title', type: 'text', placeholder: 'Mantra Japa' },
          { key: 'tool_1_description', label: 'Tool 1 — Description', type: 'textarea' },
          { key: 'tool_2_title', label: 'Tool 2 — Title', type: 'text', placeholder: 'Meditation' },
          { key: 'tool_2_description', label: 'Tool 2 — Description', type: 'textarea' },
          { key: 'tool_3_title', label: 'Tool 3 — Title', type: 'text', placeholder: 'Fire Rituals' },
          { key: 'tool_3_description', label: 'Tool 3 — Description', type: 'textarea' },
        ],
      },
      {
        title: 'Section 4 — Key Teachings & Pillar Philosophies',
        fields: [
          { key: 'teachings_overline', label: 'Section Overline', type: 'text', placeholder: 'Pillar Philosophies' },
          { key: 'teachings_heading', label: 'Section Heading', type: 'text', placeholder: 'Key Teachings & Pillar Philosophies' },
          { key: 'teaching_1_title', label: 'Card 1 — Title', type: 'text' },
          { key: 'teaching_1_description', label: 'Card 1 — Description', type: 'textarea' },
          { key: 'teaching_2_title', label: 'Card 2 — Title', type: 'text' },
          { key: 'teaching_2_description', label: 'Card 2 — Description', type: 'textarea' },
          { key: 'teaching_3_title', label: 'Card 3 — Title', type: 'text' },
          { key: 'teaching_3_description', label: 'Card 3 — Description', type: 'textarea' },
          { key: 'teaching_4_title', label: 'Card 4 — Title', type: 'text' },
          { key: 'teaching_4_description', label: 'Card 4 — Description', type: 'textarea' },
          { key: 'teaching_5_title', label: 'Card 5 — Title (optional)', type: 'text' },
          { key: 'teaching_5_description', label: 'Card 5 — Description (optional)', type: 'textarea' },
        ],
      },
      {
        title: 'Section 5 — The Four Classical Mahavakyas',
        fields: [],
        note: 'This section is hardcoded (canonical Vedic content) and cannot be edited via the Admin panel.',
      },
      {
        title: 'Section 6 — The Fifth Mahavakya (Urjjameva Brahma)',
        fields: [
          { key: 'fifth_overline', label: 'Overline', type: 'text', placeholder: 'The Fifth Mahavakya' },
          { key: 'fifth_sanskrit', label: 'Sanskrit Text', type: 'text', placeholder: 'ऊर्जैव ब्रह्म' },
          { key: 'fifth_transliteration', label: 'Transliteration', type: 'text', placeholder: 'Urjjameva Brahma' },
          { key: 'fifth_heading', label: 'Section Heading', type: 'text', placeholder: 'The Contemporary Expansion by Risheeshwar Narendranji' },
          { key: 'fifth_intro', label: 'Intro Paragraph', type: 'textarea' },
          { key: 'fifth_etym_heading', label: '1) Etymology — Heading', type: 'text', placeholder: 'Etymological Breakdown' },
          { key: 'fifth_etym_urjja', label: 'Urjja — Meaning', type: 'text' },
          { key: 'fifth_etym_eva', label: 'Eva — Meaning', type: 'text' },
          { key: 'fifth_etym_brahman', label: 'Brahman — Meaning', type: 'text' },
          { key: 'fifth_core_declaration', label: 'Core Declaration', type: 'text', placeholder: 'Verily, Cosmic Energy is Brahman.' },
          { key: 'fifth_law_heading', label: '2) Cosmic Law — Heading', type: 'text', placeholder: 'The Two-Fold Cosmic Law' },
          { key: 'fifth_law_1_title', label: 'Law 1 — Title', type: 'text' },
          { key: 'fifth_law_1_desc', label: 'Law 1 — Description', type: 'textarea' },
          { key: 'fifth_law_2_title', label: 'Law 2 — Title', type: 'text' },
          { key: 'fifth_law_2_desc', label: 'Law 2 — Description', type: 'textarea' },
          { key: 'fifth_sig_heading', label: '3) Significance — Heading', type: 'text', placeholder: 'Significance for Modern Seekers' },
          { key: 'fifth_sig_1_title', label: 'Significance 1 — Title', type: 'text' },
          { key: 'fifth_sig_1_desc', label: 'Significance 1 — Description', type: 'textarea' },
          { key: 'fifth_sig_2_title', label: 'Significance 2 — Title', type: 'text' },
          { key: 'fifth_sig_2_desc', label: 'Significance 2 — Description', type: 'textarea' },
        ],
      },
      {
        title: 'Section 7 — Unbroken Lineage & Divine Deeksha',
        fields: [
          { key: 'lineage_overline', label: 'Section Overline', type: 'text', placeholder: 'Sacred Tradition' },
          { key: 'lineage_heading', label: 'Section Heading', type: 'text', placeholder: 'Unbroken Lineage & Divine Deeksha' },
          { key: 'lineage_card_1_title', label: 'Card 1 — Title', type: 'text', placeholder: 'The Unbroken Rishi Lineage' },
          { key: 'lineage_card_1_text', label: 'Card 1 — Description', type: 'textarea' },
          { key: 'lineage_card_2_title', label: 'Card 2 — Title', type: 'text', placeholder: 'The Grace of Maharishi Agastya' },
          { key: 'lineage_card_2_text', label: 'Card 2 — Description', type: 'textarea' },
        ],
      },
    ],
  },
  gallery: {
    label: 'Gallery Page',
    sections: [
      {
        title: 'Hero Section',
        fields: [
          { key: 'hero_overline', label: 'Hero Overline', type: 'text', placeholder: 'Spiritual Resources' },
          { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'Gallery & Resources' },
          { key: 'hero_description', label: 'Hero Description', type: 'textarea' },
        ],
      },
      {
        title: 'Section Headings',
        fields: [
          { key: 'videos_heading', label: 'Videos Section Heading', type: 'text', placeholder: 'Watch & Learn' },
          { key: 'articles_heading', label: 'Articles Section Heading', type: 'text', placeholder: 'Sacred Articles & Wisdom' },
        ],
      },
    ],
  },
};

// ---------- Legal / Policy page schema builder ----------
const LEGAL_TEXT_TIPS =
  'Formatting tips: use a blank line to start a new paragraph, **wrap text in double asterisks** to make it bold, and any email address auto-links.';

const buildLegalSchema = (label, sectionTitles) => ({
  label,
  sections: [
    {
      title: 'Page Header',
      fields: [
        { key: 'overline', label: 'Overline (small caps above title)', type: 'text', placeholder: 'Legal' },
        { key: 'title', label: 'Page Title', type: 'text', placeholder: label },
        { key: 'last_updated', label: 'Last Updated', type: 'text', placeholder: 'February 2026' },
        { key: 'intro', label: 'Intro / Preamble (optional, shown in a highlighted callout)', type: 'textarea' },
      ],
    },
    ...sectionTitles.map((defaultHeading, i) => ({
      title: `Section ${i + 1}`,
      fields: [
        { key: `section_${i + 1}_heading`, label: `Section ${i + 1} — Heading`, type: 'text', placeholder: defaultHeading },
        { key: `section_${i + 1}_body`, label: `Section ${i + 1} — Body`, type: 'textarea', placeholder: LEGAL_TEXT_TIPS },
      ],
    })),
  ],
});

PAGE_SCHEMAS.terms = buildLegalSchema('Terms & Conditions Page', [
  'General',
  'Payments & Donations',
  'Intellectual Property',
  'Limitation of Liability',
  'Contact Information',
]);

PAGE_SCHEMAS.privacy = buildLegalSchema('Privacy Policy Page', [
  'Information Collected',
  'How We Use Information',
  'Data Security & Payment Gateway',
  'Sharing of Information',
  'Contact Us',
]);

PAGE_SCHEMAS.refund = buildLegalSchema('Refund Policy Page', [
  'Donations',
  'Courses & Event Fees',
  'Contact for Refund Requests',
]);

PAGE_SCHEMAS.cancellation = buildLegalSchema('Cancellation Policy Page', [
  'Donation Cancellations',
  'Course / Event Registration Cancellations',
  'Organization Cancellations',
]);

PAGE_SCHEMAS.publications = {
  label: 'Publications Page',
  sections: [
    {
      title: 'Hero Section',
      fields: [
        { key: 'hero_overline', label: 'Hero Overline', type: 'text', placeholder: 'Spiritual Research & Empirical Studies' },
        { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'Bridging Ancient Wisdom with Modern Inquiry' },
        { key: 'hero_description', label: 'Hero Description', type: 'textarea' },
      ],
    },
    {
      title: 'Methodology',
      fields: [
        { key: 'method_overline', label: 'Overline', type: 'text', placeholder: 'Our Methodology' },
        { key: 'method_heading', label: 'Section Heading', type: 'text', placeholder: 'Research Guided by Risheeshwar Narendranji' },
        { key: 'method_description', label: 'Section Description', type: 'textarea' },
        { key: 'method_1_title', label: 'Card 1 — Title', type: 'text' },
        { key: 'method_1_desc', label: 'Card 1 — Description', type: 'textarea' },
        { key: 'method_2_title', label: 'Card 2 — Title', type: 'text' },
        { key: 'method_2_desc', label: 'Card 2 — Description', type: 'textarea' },
        { key: 'method_3_title', label: 'Card 3 — Title', type: 'text' },
        { key: 'method_3_desc', label: 'Card 3 — Description', type: 'textarea' },
      ],
    },
    {
      title: 'Archive & CTA',
      fields: [
        { key: 'archive_heading', label: 'Archive Section Heading', type: 'text', placeholder: 'Research Archive' },
        { key: 'cta_heading', label: 'CTA Heading', type: 'text', placeholder: 'Join Our Research Circle' },
        { key: 'cta_description', label: 'CTA Description', type: 'textarea' },
      ],
    },
  ],
};

export const PageContentEditor = ({ pageId }) => {
  const activePage = pageId || 'home';
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent(activePage);
  }, [activePage]);

  const fetchContent = async (id) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/pages/${id}`);
      setContent(data.content || {});
    } catch (e) {
      setContent({});
      toast.error('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/pages/${activePage}`,
        { content },
        { withCredentials: true }
      );
      toast.success(`${PAGE_SCHEMAS[activePage].label} saved successfully!`);
    } catch (e) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const schema = PAGE_SCHEMAS[activePage];
  if (!schema) return null;

  return (
    <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6" data-testid={`page-content-editor-${activePage}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading text-charcoal-800">Edit {schema.label}</h2>
          <p className="text-sm text-charcoal-500 mt-1">
            Update headlines, text, and images. Leave fields blank to use defaults.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchContent(activePage)}
            data-testid="page-reload-button"
            className="flex items-center gap-2 border border-charcoal-200 text-charcoal-700 rounded-lg px-4 py-2 hover:bg-charcoal-50 transition-colors"
          >
            <RefreshCw size={18} /> Reload
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="page-save-button"
            className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-6 py-2 hover:bg-saffron-500 transition-colors disabled:opacity-50"
          >
            <Save size={18} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-charcoal-500">Loading...</div>
      ) : (
        <div className="space-y-8">
          {schema.sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-lg font-heading text-saffron-700 mb-4 pb-2 border-b border-saffron-100">
                {section.title}
              </h3>
              {section.note && (
                <p className="text-sm text-charcoal-500 italic mb-3 bg-saffron-50 border border-saffron-100 rounded-md px-3 py-2">
                  {section.note}
                </p>
              )}
              <div className="space-y-4">
                {section.fields.map((field) => (
                  <div key={field.key}>
                    {field.type === 'image' ? (
                      <ImageUpload
                        label={field.label}
                        recommendedSize={field.recommended}
                        value={content[field.key]}
                        onChange={(v) => handleChange(field.key, v)}
                        testId={`page-field-${field.key}`}
                      />
                    ) : (
                      <>
                        <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">
                          {field.label}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            rows={3}
                            value={content[field.key] || ''}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            placeholder={field.placeholder || 'Leave blank for default'}
                            data-testid={`page-field-${field.key}`}
                            className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                          />
                        ) : (
                          <input
                            type="text"
                            value={content[field.key] || ''}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            placeholder={field.placeholder || 'Leave blank for default'}
                            data-testid={`page-field-${field.key}`}
                            className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                          />
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
